import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  buildMeasuredReportPages,
  buildReportPages,
  type ReportLayoutMeasurements,
} from "./reportLayout";
import { REPORT_PRINT_CSS } from "./reportPrint.css";
import type {
  ReportCell,
  ReportColumn,
  ReportDocumentModel,
  ReportFooterModel,
  ReportHeaderModel,
  ReportMetric,
  ReportSectionSegment,
  ReportTableRow,
} from "./reportTypes";

type ReportPrintMode = "source" | "preview";

export function ReportDocument({
  document,
  printMode = "source",
}: {
  document: ReportDocumentModel;
  printMode?: ReportPrintMode;
}) {
  const measurementRootRef = useRef<HTMLDivElement | null>(null);
  const estimatedPages = useMemo(() => buildReportPages(document), [document]);
  const [measuredPages, setMeasuredPages] = useState<
    typeof estimatedPages | null
  >(null);
  const pages = measuredPages ?? estimatedPages;
  const metricRows = reportMetricRows(document);
  const documentClassName = [
    "report-print-document",
    printMode === "source" ? "report-print-source-document" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const expectedRowCount = countDocumentRows(document);
  const renderedRowCount = countPageRows(pages);

  useLayoutEffect(() => {
    setMeasuredPages(null);
    const measurements = collectReportMeasurements(
      measurementRootRef.current,
      document,
    );
    if (!measurements) return;

    setMeasuredPages(buildMeasuredReportPages(document, measurements));
  }, [document]);

  return (
    <div
      className={documentClassName}
      data-report-expected-row-count={expectedRowCount}
      data-report-layout={measuredPages ? "measured" : "estimated"}
      data-report-rendered-row-count={renderedRowCount}
    >
      <style>{REPORT_PRINT_CSS}</style>
      {pages.map((page) => (
        <section
          className="report-print-page"
          data-testid="report-print-page"
          key={page.index}
        >
          {page.showHeader && (
            <>
              <ReportHeader header={document.header} />
              {metricRows.length > 0 && (
                <ReportMetrics metricRows={metricRows} />
              )}
            </>
          )}

          {page.contextLabel && (
            <div className="report-print-page-context">
              <span>{page.contextLabel}</span>
              <span>{`Pagina ${page.index + 1}`}</span>
            </div>
          )}

          <main className="report-print-body">
            {page.blocks.map((block) => (
              <ReportSection key={block.id} section={block.section} />
            ))}
          </main>

          {page.showFooter && <ReportFooter footer={document.footer} />}
        </section>
      ))}
      <ReportMeasurementDocument
        document={document}
        measureRef={measurementRootRef}
        metricRows={metricRows}
      />
    </div>
  );
}

function reportMetricRows(document: ReportDocumentModel) {
  return document.metricRows ?? (document.metrics ? [document.metrics] : []);
}

function ReportHeader({ header }: { header: ReportHeaderModel }) {
  return (
    <header className="report-print-header">
      <div className="report-print-brand">
        <div className="report-print-brand-mark">
          {header.logoSvg && (
            <img
              alt={header.brand}
              className="report-print-brand-logo report-print-logo"
              src={svgDataUri(header.logoSvg)}
            />
          )}
          <span className="report-print-brand-text">{header.brand}</span>
        </div>
        <span>{header.documentLabel}</span>
      </div>
      <div className="report-print-title-row">
        <div>
          <h1>{header.title}</h1>
          <p>{header.subtitle}</p>
        </div>
        <div className="report-print-date">
          <strong>{header.dateLabel}</strong>
          <span>{header.dateSubLabel}</span>
        </div>
      </div>
    </header>
  );
}

function svgDataUri(svg: string) {
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function ReportMetrics({ metricRows }: { metricRows: ReportMetric[][] }) {
  return (
    <div className="report-print-metrics">
      {metricRows.map((row, rowIndex) => (
        <div
          className={metricRowClassName(
            rowIndex,
            metricRows.length,
            row.length,
          )}
          key={`metric-row-${rowIndex}`}
        >
          {row.map((metric, index) => (
            <div
              className={[
                "report-print-metric",
                metric.tone === "highlight"
                  ? "report-print-metric-highlight"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
              key={`${metric.label}-${index}`}
            >
              <span>{metric.label}</span>
              <strong className="report-print-metric-value">
                {metric.value}
              </strong>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function metricRowClassName(
  rowIndex: number,
  rowCount: number,
  metricCount: number,
) {
  return [
    "report-print-metric-row",
    rowCount === 1 ? "report-print-metric-row-legacy" : "",
    rowCount > 1 && rowIndex === 0 ? "report-print-metric-row-volume" : "",
    rowCount > 1 && rowIndex > 0 ? "report-print-metric-row-total" : "",
    `report-print-metric-row-count-${metricCount}`,
  ]
    .filter(Boolean)
    .join(" ");
}

function ReportSection({
  measurement,
  section,
}: {
  measurement?: { sectionIndex: number; continuation: boolean };
  section: ReportSectionSegment;
}) {
  return (
    <section
      className="report-print-section"
      data-report-measure-continuation={
        measurement ? String(measurement.continuation) : undefined
      }
      data-report-measure-section-index={measurement?.sectionIndex}
    >
      {section.showHeader && (
        <div className="report-print-section-head">
          <div>
            <strong>{section.title}</strong>
            {section.subtitle && <span>{section.subtitle}</span>}
          </div>
          {section.summary && (
            <div className="report-print-section-summary">
              <span className="report-print-summary-volume">
                {section.summary.volume}
              </span>
              <span className="report-print-summary-money">
                {section.summary.money}
              </span>
            </div>
          )}
          {!section.summary && section.aside && <b>{section.aside}</b>}
        </div>
      )}
      <table className="report-print-table">
        <colgroup>
          {section.columns.map((column) => (
            <col key={column.key} style={{ width: column.width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {section.columns.map((column) => (
              <th className={columnClassName(column)} key={column.key}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {section.rows.map((row, rowIndex) => (
            <ReportRow
              columns={section.columns}
              key={row.id}
              row={row}
              rowIndex={rowIndex}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ReportRow({
  columns,
  row,
  rowIndex,
}: {
  columns: ReportColumn[];
  row: ReportTableRow;
  rowIndex: number;
}) {
  const rowClassName = [
    `report-print-row-${row.density ?? "normal"}`,
    row.tone === "highlight" ? "report-print-row-highlight" : "",
    row.tone === "muted" ? "report-print-row-muted" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <tr
      className={rowClassName}
      data-report-measure-row-index={rowIndex}
      data-report-row="true"
    >
      {columns.map((column) => {
        const cell = findCell(row, column.key);

        return (
          <td className={cellClassName(column, cell)} key={column.key}>
            {cell?.value ?? ""}
            {cell?.secondary && (
              <span className="report-print-cell-secondary">
                {cell.secondary}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

function findCell(row: ReportTableRow, columnKey: string) {
  return row.cells.find((cell) => cell.columnKey === columnKey);
}

function ReportFooter({ footer }: { footer: ReportFooterModel }) {
  return (
    <footer className="report-print-footer" data-testid="report-print-footer">
      <strong>{footer.brand}</strong>
      <span>{`${footer.generatedLabel} · ${footer.documentNumber}`}</span>
    </footer>
  );
}

function columnClassName(column: ReportColumn) {
  return [
    column.align === "right" ? "report-print-cell-align-right" : "",
    column.noWrap ? "report-print-cell-nowrap" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function cellClassName(column: ReportColumn, cell: ReportCell | undefined) {
  const align = cell?.align ?? column.align;

  return [
    "report-print-table-cell",
    align === "right" ? "report-print-cell-align-right" : "",
    align === "right" ? "report-print-table-cell--numeric" : "",
    column.noWrap ? "report-print-cell-nowrap" : "",
    cell?.emphasis ? "report-print-cell-emphasis" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function countDocumentRows(document: ReportDocumentModel) {
  return document.sections.reduce(
    (sum, section) => sum + section.rows.length,
    0,
  );
}

function countPageRows(pages: ReturnType<typeof buildReportPages>) {
  return pages.reduce(
    (pageSum, page) =>
      pageSum +
      page.blocks.reduce(
        (blockSum, block) => blockSum + block.section.rows.length,
        0,
      ),
    0,
  );
}

function ReportMeasurementDocument({
  document,
  measureRef,
  metricRows,
}: {
  document: ReportDocumentModel;
  measureRef: RefObject<HTMLDivElement | null>;
  metricRows: ReportMetric[][];
}) {
  if (isJsdomEnvironment()) {
    return (
      <div
        aria-hidden="true"
        className="report-print-document report-print-measure-document"
        ref={measureRef}
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      className="report-print-document report-print-measure-document"
      ref={measureRef}
    >
      <section className="report-print-page" data-report-measure-page="true">
        <ReportHeader header={document.header} />
        {metricRows.length > 0 && <ReportMetrics metricRows={metricRows} />}
        <main
          className="report-print-body"
          data-report-measure-body-default="true"
        />
        {document.continuationContext && (
          <>
            <div
              className="report-print-page-context"
              data-report-measure-context="true"
            >
              <span>{document.continuationContext}</span>
              <span>Pagina 2</span>
            </div>
            <main
              className="report-print-body"
              data-report-measure-body-context="true"
            />
          </>
        )}
        <ReportFooter footer={document.footer} />
      </section>
      <section
        className="report-print-page"
        data-report-measure-sections-page="true"
      >
        <main className="report-print-body">
          {document.sections.map((section, sectionIndex) => (
            <div key={`measure-${section.id}-${sectionIndex}`}>
              <ReportSection
                measurement={{ sectionIndex, continuation: false }}
                section={{
                  ...section,
                  continuation: false,
                  showHeader: true,
                }}
              />
              <ReportSection
                measurement={{ sectionIndex, continuation: true }}
                section={{
                  ...section,
                  continuation: true,
                  showHeader: false,
                }}
              />
            </div>
          ))}
        </main>
      </section>
    </div>
  );
}

function isJsdomEnvironment() {
  return (
    typeof navigator !== "undefined" &&
    navigator.userAgent.toLowerCase().includes("jsdom")
  );
}

function collectReportMeasurements(
  root: HTMLDivElement | null,
  document: ReportDocumentModel,
): ReportLayoutMeasurements | null {
  if (!root) return null;

  const page = root.querySelector<HTMLElement>("[data-report-measure-page]");
  const defaultBody = root.querySelector<HTMLElement>(
    "[data-report-measure-body-default]",
  );
  const contextBody = root.querySelector<HTMLElement>(
    "[data-report-measure-body-context]",
  );
  const header = root.querySelector<HTMLElement>(".report-print-header");
  const metrics = root.querySelector<HTMLElement>(".report-print-metrics");
  const context = root.querySelector<HTMLElement>(
    "[data-report-measure-context]",
  );
  const footer = root.querySelector<HTMLElement>(".report-print-footer");

  if (!page || !defaultBody || !header || !footer) return null;

  const pageHeight = elementHeight(page);
  const headerHeight = elementHeight(header);
  const metricHeight = metrics ? elementHeight(metrics) : 0;
  const footerHeight = elementHeight(footer);
  const defaultPadding = verticalPadding(defaultBody);
  const contextPadding = verticalPadding(contextBody ?? defaultBody);
  const contextHeight = context ? elementHeight(context) : 0;

  if (
    pageHeight <= 0 ||
    headerHeight <= 0 ||
    footerHeight <= 0 ||
    defaultPadding <= 0
  ) {
    return null;
  }

  return {
    firstPageAvailableHeight:
      pageHeight - headerHeight - metricHeight - defaultPadding,
    continuationPageAvailableHeight:
      pageHeight - contextHeight - contextPadding,
    plainPageAvailableHeight: pageHeight - defaultPadding,
    footerHeight,
    sections: document.sections.map((_, sectionIndex) =>
      collectSectionMeasurements(root, sectionIndex),
    ),
  };
}

function collectSectionMeasurements(
  root: HTMLDivElement,
  sectionIndex: number,
) {
  const first = root.querySelector<HTMLElement>(
    `[data-report-measure-section-index="${sectionIndex}"][data-report-measure-continuation="false"]`,
  );
  const continuation = root.querySelector<HTMLElement>(
    `[data-report-measure-section-index="${sectionIndex}"][data-report-measure-continuation="true"]`,
  );
  const firstRows = Array.from(
    first?.querySelectorAll<HTMLElement>("tr[data-report-row='true']") ?? [],
  );
  const continuationRows = Array.from(
    continuation?.querySelectorAll<HTMLElement>("tr[data-report-row='true']") ??
      [],
  );
  const rowHeights = firstRows.map(elementOuterHeight);
  const continuationRowHeights = continuationRows.map(elementOuterHeight);

  return {
    firstSegmentShellHeight: first
      ? Math.max(elementOuterHeight(first) - sum(rowHeights), 0)
      : 0,
    continuationSegmentShellHeight: continuation
      ? Math.max(
          elementOuterHeight(continuation) - sum(continuationRowHeights),
          0,
        )
      : 0,
    rowHeights,
  };
}

function elementHeight(element: HTMLElement) {
  return element.getBoundingClientRect().height;
}

function elementOuterHeight(element: HTMLElement) {
  const style = getComputedStyle(element);
  return (
    elementHeight(element) +
    numericStyleValue(style.marginTop) +
    numericStyleValue(style.marginBottom)
  );
}

function verticalPadding(element: HTMLElement) {
  const style = getComputedStyle(element);
  return (
    numericStyleValue(style.paddingTop) + numericStyleValue(style.paddingBottom)
  );
}

function numericStyleValue(value: string) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sum(values: number[]) {
  return values.reduce((total, value) => total + value, 0);
}
